import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { status, data } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) {
      const e = data.detail[0];
      return typeof e === 'object' ? `${e.loc?.[e.loc.length - 1] || 'Field'}: ${e.msg || 'Invalid'}` : String(e);
    }
    if (typeof data.detail === 'string') return data.detail;
  }
  return data.detail || data.message || `Request failed (${status})`;
};

const PurchaseOrderForm = ({ onClose, onSuccess }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    expected_delivery_date: '',
    gst_rate: '',
    notes: '',
  });
  const [items, setItems] = useState([{ product_id: '', unit_id: '', quantity_ordered: '', unit_cost: '', discount_percent: 0 }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [supRes, prodRes] = await Promise.all([
          api.get('/suppliers?is_active=true'),
          api.get('/products')
        ]);
        setSuppliers(supRes.data);
        setProducts(prodRes.data.items ?? prodRes.data.products ?? []);
      } catch (err) { console.error('Failed to load data:', err); }
    };
    load();
  }, []);

  const addItem = () => setItems([...items, { product_id: '', unit_id: '', quantity_ordered: '', unit_cost: '', discount_percent: 0 }]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      updated[idx].unit_id = '';
      if (prod) {
        const defaultUnit = (prod.units || []).find(u => u.is_default);
        if (defaultUnit) {
          updated[idx].unit_id = defaultUnit.id;
          updated[idx].unit_cost = defaultUnit.purchase_price || prod.unit_cost || 0;
        } else {
          updated[idx].unit_cost = prod.unit_cost;
        }
      }
    }
    if (field === 'unit_id') {
      const prod = products.find(p => p.id === updated[idx].product_id);
      if (prod) {
        if (value) {
          const unit = (prod.units || []).find(u => u.id === value);
          if (unit) updated[idx].unit_cost = unit.purchase_price || prod.unit_cost || 0;
        } else {
          updated[idx].unit_cost = prod.unit_cost;
        }
      }
    }
    setItems(updated);
  };

  const calcLineTotal = (item) => {
    const qty = parseFloat(item.quantity_ordered) || 0;
    const cost = parseFloat(item.unit_cost) || 0;
    const disc = parseFloat(item.discount_percent) || 0;
    return qty * cost * (1 - disc / 100);
  };
  const subtotal = items.reduce((sum, item) => sum + calcLineTotal(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.supplier_id) { setError('Select a supplier'); return; }
    if (items.some(i => !i.product_id || !i.quantity_ordered || !i.unit_cost)) { setError('All items must have product, quantity, and unit cost'); return; }
    setLoading(true);
    try {
      await api.post('/purchase-orders', {
        ...formData,
        gst_rate: formData.gst_rate !== '' ? parseFloat(formData.gst_rate) : null,
        items: items.map(i => ({
          product_id: i.product_id,
          unit_id: i.unit_id || null,
          quantity_ordered: i.quantity_ordered,
          unit_cost: parseFloat(i.unit_cost),
          discount_percent: parseFloat(i.discount_percent) || 0,
        })),
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const itemInputClass = 'w-full bg-white border border-[#d1d5db] rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none';

  return (
    <ModalShell
      title="New Purchase Order"
      onClose={onClose}
      maxWidth="max-w-4xl"
      dataTestId="po-form-modal"
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm font-semibold text-[#1a1a2e]">Subtotal: Rs. {subtotal.toLocaleString()}</div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-po-form">Cancel</button>
            <button type="submit" form="po-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-po-button">{loading ? 'Creating...' : 'Create Purchase Order'}</button>
          </div>
        </div>
      }
    >
      <form id="po-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="po-form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className={labelClass}>Supplier {requiredMark}</label>
            <select className={selectClass} value={formData.supplier_id} onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })} required data-testid="po-supplier-select">
              <option value="">Select Supplier</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Order Date</label>
            <input type="date" className={inputClass} value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} data-testid="po-order-date" />
          </div>
          <div>
            <label className={labelClass}>Expected Delivery</label>
            <input type="date" className={inputClass} value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} data-testid="po-expected-date" />
          </div>
          <div>
            <label className={labelClass}>GST %</label>
            <input type="number" min="0" max="100" step="0.01" placeholder="Tenant default" className={inputClass} value={formData.gst_rate} onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })} data-testid="po-gst-rate" />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-semibold text-[#1a1a2e]">Items</label>
            <button type="button" onClick={addItem} className="text-[#4A7AE0] text-sm font-medium flex items-center gap-1 hover:underline" data-testid="po-add-item">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="bg-white border border-[#d1d5db] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f9fafb] text-[#6b7280]">
                <tr>
                  <th className="py-2 px-3 text-left">Product</th>
                  <th className="py-2 px-3 text-left w-32">Unit</th>
                  <th className="py-2 px-3 text-left w-24">Qty</th>
                  <th className="py-2 px-3 text-left w-28">Unit Cost</th>
                  <th className="py-2 px-3 text-left w-20">Disc %</th>
                  <th className="py-2 px-3 text-right w-28">Total</th>
                  <th className="py-2 px-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const prod = products.find(p => p.id === item.product_id);
                  const prodUnits = prod?.units || [];
                  const baseUnitLabel = prod?.base_unit || prod?.unit || '';
                  return (
                    <tr key={idx} className="border-t border-[#e5e7eb]">
                      <td className="py-2 px-3">
                        <select className={itemInputClass} value={item.product_id} onChange={(e) => updateItem(idx, 'product_id', e.target.value)} data-testid={`po-item-product-${idx}`}>
                          <option value="">Select</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-3">
                        <select className={`${itemInputClass} disabled:bg-[#f3f4f6]`} value={item.unit_id} onChange={(e) => updateItem(idx, 'unit_id', e.target.value)} disabled={!item.product_id} data-testid={`po-item-unit-${idx}`}>
                          <option value="">{baseUnitLabel || 'Base'}</option>
                          {prodUnits.map(u => <option key={u.id} value={u.id}>{u.unit_name} ({u.conversion_factor} {baseUnitLabel})</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-3"><input type="number" min="0.01" step="0.01" className={itemInputClass} value={item.quantity_ordered} onChange={(e) => updateItem(idx, 'quantity_ordered', e.target.value)} data-testid={`po-item-qty-${idx}`} /></td>
                      <td className="py-2 px-3"><input type="number" min="0" step="0.01" className={itemInputClass} value={item.unit_cost} onChange={(e) => updateItem(idx, 'unit_cost', e.target.value)} data-testid={`po-item-cost-${idx}`} /></td>
                      <td className="py-2 px-3"><input type="number" min="0" max="100" step="0.01" className={itemInputClass} value={item.discount_percent} onChange={(e) => updateItem(idx, 'discount_percent', e.target.value)} data-testid={`po-item-disc-${idx}`} /></td>
                      <td className="py-2 px-3 text-right font-medium">Rs. {calcLineTotal(item).toLocaleString()}</td>
                      <td className="py-2 px-3">
                        {items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="text-[#EF4444] hover:text-red-700" data-testid={`po-remove-item-${idx}`}><Trash2 className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={textareaClass} rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="po-notes" />
        </div>
      </form>
    </ModalShell>
  );
};

export default PurchaseOrderForm;
